import { useEffect, useId } from "react";
import { FieldError, useForm } from "react-hook-form";

import { useTranslation } from "next-i18next";

import { DeliveryQuoteDeliveryType } from "@/Apollo/schema";
import { US_STATES } from "@/features/us-delivery/static/us-states.static";
import {
  UsQuoteFormValues,
  usQuoteFormSchema,
} from "@/features/us-delivery/us-delivery.utils";
import { Button } from "@/ui/cva/Button";
import { FormGroup } from "@/ui/cva/FormGroup";
import { Input } from "@/ui/cva/input/Input";
import { InputGroupHelper } from "@/ui/cva/input/InputGroupHelper";
import { TextArea } from "@/ui/cva/input/TextArea";
import { RadioButtonLabel } from "@/ui/cva/radio-button/RadioButtonLabel";
import { Select } from "@/ui/cva/select/Select";
import { Flavor } from "@/ui/typography/Flavor";
import { InputLabel } from "@/ui/typography/InputLabel";
import { zodResolver } from "@hookform/resolvers/zod";

type UsDeliveryQuoteFormProps = {
  /** The previous quote's address, when asking again. */
  defaultValues?: Partial<UsQuoteFormValues>;
  submitting: boolean;
  /** The refused request's message, shown above the buttons. */
  requestError?: string;
  onSubmit: (values: UsQuoteFormValues) => void;
  onCancel: () => void;
};

/**
 * WHO-6509, state B of WHO-6499: where the item should go. A ZIP code, state and city are what
 * ops need to ask the courier; the street is not asked (open with Sterre, WHO-6499).
 */
export const UsDeliveryQuoteForm = ({
  defaultValues,
  submitting,
  requestError,
  onSubmit,
  onCancel,
}: UsDeliveryQuoteFormProps) => {
  const { t } = useTranslation();
  const postalCodeId = useId();
  const regionId = useId();
  const cityId = useId();
  const noteId = useId();
  const errorId = useId();

  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
  } = useForm<UsQuoteFormValues>({
    defaultValues: {
      deliveryType: DeliveryQuoteDeliveryType.Door,
      ...defaultValues,
    },
    resolver: zodResolver(usQuoteFormSchema),
  });

  /* The form replaces the button that opened it, so focus moves into it rather than to the page. */
  useEffect(() => {
    setFocus("postalCode");
  }, [setFocus]);

  const fieldErrorCopy: Record<keyof UsQuoteFormValues, string> = {
    postalCode: t("pdp:pdp-us-delivery-error-zip", {
      defaultValue: "Enter a five-digit ZIP code.",
    }),
    region: t("pdp:pdp-us-delivery-error-state", {
      defaultValue: "Choose a state.",
    }),
    city: t("pdp:pdp-us-delivery-error-city", {
      defaultValue: "Enter the city.",
    }),
    deliveryType: t("pdp:pdp-us-delivery-error-type", {
      defaultValue: "Choose how it should be delivered.",
    }),
    note: t("pdp:pdp-us-delivery-error-note", {
      defaultValue: "Keep the note under 1,000 characters.",
    }),
  };

  const fieldErrorId = (field: keyof UsQuoteFormValues) =>
    `${errorId}-${field}`;

  /* Ties a field to its error, so a screen reader reads the error with the field. */
  const describedBy = (field: keyof UsQuoteFormValues, error?: FieldError) =>
    error
      ? { "aria-invalid": true, "aria-describedby": fieldErrorId(field) }
      : {};

  const renderError = (field: keyof UsQuoteFormValues, error?: FieldError) =>
    error ? (
      <span id={fieldErrorId(field)}>
        <InputGroupHelper valid={false}>
          {fieldErrorCopy[field]}
        </InputGroupHelper>
      </span>
    ) : null;

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-4"
      data-testid="pdp.us-delivery.form"
    >
      <Flavor size="medium" className="font-bold">
        {t("pdp:pdp-us-delivery-form-title", {
          defaultValue: "Where should it go?",
        })}
      </Flavor>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormGroup
          id={postalCodeId}
          valid={!errors.postalCode}
          label={props => (
            <InputLabel {...props}>
              {t("pdp:pdp-us-delivery-form-zip", { defaultValue: "ZIP code" })}
            </InputLabel>
          )}
          element={props => (
            <Input
              {...props}
              inputMode="numeric"
              autoComplete="postal-code"
              placeholder="10013"
              {...describedBy("postalCode", errors.postalCode)}
              {...register("postalCode")}
            />
          )}
          helper={renderError("postalCode", errors.postalCode)}
        />
        <FormGroup
          id={regionId}
          valid={!errors.region}
          label={props => (
            <InputLabel {...props}>
              {t("pdp:pdp-us-delivery-form-state", { defaultValue: "State" })}
            </InputLabel>
          )}
          element={props => (
            <Select
              {...props}
              autoComplete="address-level1"
              defaultValue=""
              {...describedBy("region", errors.region)}
              {...register("region")}
            >
              <option value="" disabled>
                {t("pdp:pdp-us-delivery-form-state-placeholder", {
                  defaultValue: "Choose a state",
                })}
              </option>
              {US_STATES.map(state => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </Select>
          )}
          helper={renderError("region", errors.region)}
        />
      </div>
      <FormGroup
        id={cityId}
        valid={!errors.city}
        label={props => (
          <InputLabel {...props}>
            {t("pdp:pdp-us-delivery-form-city", { defaultValue: "City" })}
          </InputLabel>
        )}
        element={props => (
          <Input
            {...props}
            autoComplete="address-level2"
            {...describedBy("city", errors.city)}
            {...register("city")}
          />
        )}
        helper={renderError("city", errors.city)}
      />
      <fieldset
        className="flex flex-col space-y-1"
        // `aria-invalid` is not a state a group supports; the description is enough.
        aria-describedby={
          errors.deliveryType ? fieldErrorId("deliveryType") : undefined
        }
      >
        <legend className="font-body text-base text-black">
          {t("pdp:pdp-us-delivery-form-type", { defaultValue: "Delivery" })}
        </legend>
        <div className="flex flex-col gap-2">
          <RadioButtonLabel
            valid={!errors.deliveryType}
            label={t("pdp:pdp-us-delivery-type-door", {
              defaultValue: "To the door",
            })}
            value={DeliveryQuoteDeliveryType.Door}
            {...register("deliveryType")}
          />
          <RadioButtonLabel
            valid={!errors.deliveryType}
            label={t("pdp:pdp-us-delivery-type-room", {
              defaultValue: "Into the room",
            })}
            value={DeliveryQuoteDeliveryType.Room}
            {...register("deliveryType")}
          />
        </div>
        {renderError("deliveryType", errors.deliveryType)}
      </fieldset>
      <FormGroup
        id={noteId}
        valid={!errors.note}
        label={props => (
          <InputLabel {...props}>
            {t("pdp:pdp-us-delivery-form-note", {
              defaultValue: "Anything we should know? (optional)",
            })}
          </InputLabel>
        )}
        element={props => (
          <TextArea
            {...props}
            rows={3}
            {...describedBy("note", errors.note)}
            {...register("note")}
          />
        )}
        helper={renderError("note", errors.note)}
      />
      {requestError && (
        <div role="alert">
          <InputGroupHelper valid={false}>{requestError}</InputGroupHelper>
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        <Button type="submit" loading={submitting} disabled={submitting}>
          {t("pdp:pdp-us-delivery-request-btn", {
            defaultValue: "Request a quote",
          })}
        </Button>
        <Button
          type="button"
          appearance="minimal"
          onClick={onCancel}
          disabled={submitting}
        >
          {t("pdp:pdp-us-delivery-form-cancel", { defaultValue: "Cancel" })}
        </Button>
      </div>
    </form>
  );
};
